// Performance monitoring and Core Web Vitals tracking

// Type declarations for Performance API
declare global {
  interface LargestContentfulPaint extends PerformanceEntry {
    readonly startTime: number;
  }

  interface PerformanceEventTiming extends PerformanceEntry {
    readonly processingStart: number;
    readonly startTime: number;
  }

  interface LayoutShift extends PerformanceEntry {
    readonly value: number;
    readonly hadRecentInput: boolean;
  }

  interface PerformancePaintTiming extends PerformanceEntry {
    readonly name: string;
    readonly startTime: number;
  }

  interface PerformanceLongTaskTiming extends PerformanceEntry {
    readonly duration: number;
  }

  interface PerformanceMemory {
    readonly usedJSHeapSize: number;
    readonly totalJSHeapSize: number;
  }

  interface Performance {
    readonly memory?: PerformanceMemory;
  }

  interface Window {
    gtag?: (command: string, targetId: string, config?: unknown) => void;
    analytics?: {
      track: (event: string, properties: Record<string, unknown>) => void;
    };
  }
}

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  url: string;
  userAgent: string;
}

interface WebVitalsMetric extends PerformanceMetric {
  id: string;
  delta: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

interface PerformanceSummary {
  count: number;
  total: number;
  min: number;
  max: number;
  avg: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private observers: PerformanceObserver[] = [];
  private isEnabled: boolean = true;

  constructor() {
    this.setupPerformanceObservers();
    this.trackNavigationTiming();
    this.trackResourceTiming();
  }

  // Core Web Vitals tracking
  trackWebVitals(): void {
    if (!this.isEnabled) return;

    // Track Largest Contentful Paint (LCP)
    this.trackLCP();

    // Track First Input Delay (FID)
    this.trackFID();

    // Track Cumulative Layout Shift (CLS)
    this.trackCLS();

    // Track First Contentful Paint (FCP)
    this.trackFCP();

    // Track Time to First Byte (TTFB)
    this.trackTTFB();
  }

  private trackLCP(): void {
    try {
      const observer = new PerformanceObserver(list => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as PerformanceEntry;

        if (lastEntry) {
          this.recordMetric({
            name: 'LCP',
            value: lastEntry.startTime,
            timestamp: Date.now(),
            url: window.location.href,
            userAgent: navigator.userAgent,
          });
        }
      });

      observer.observe({ entryTypes: ['largest-contentful-paint'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('LCP tracking not supported:', error);
    }
  }

  private trackFID(): void {
    try {
      const observer = new PerformanceObserver(list => {
        const entries = list.getEntries();
        entries.forEach((entry: PerformanceEntry) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const perfEntry = entry as any;
          this.recordMetric({
            name: 'FID',
            value: perfEntry.processingStart - perfEntry.startTime,
            timestamp: Date.now(),
            url: window.location.href,
            userAgent: navigator.userAgent,
          });
        });
      });

      observer.observe({ entryTypes: ['first-input'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('FID tracking not supported:', error);
    }
  }

  private trackCLS(): void {
    try {
      let clsValue = 0;
      const observer = new PerformanceObserver(list => {
        const entries = list.getEntries();
        entries.forEach((entry: PerformanceEntry) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const perfEntry = entry as any;
          if (!perfEntry.hadRecentInput) {
            clsValue += perfEntry.value;
          }
        });

        this.recordMetric({
          name: 'CLS',
          value: clsValue,
          timestamp: Date.now(),
          url: window.location.href,
          userAgent: navigator.userAgent,
        });
      });

      observer.observe({ entryTypes: ['layout-shift'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('CLS tracking not supported:', error);
    }
  }

  private trackFCP(): void {
    try {
      const observer = new PerformanceObserver(list => {
        const entries = list.getEntries();
        entries.forEach((entry: PerformanceEntry) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const perfEntry = entry as any;
          if (perfEntry.name === 'first-contentful-paint') {
            this.recordMetric({
              name: 'FCP',
              value: perfEntry.startTime,
              timestamp: Date.now(),
              url: window.location.href,
              userAgent: navigator.userAgent,
            });
          }
        });
      });

      observer.observe({ entryTypes: ['paint'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('FCP tracking not supported:', error);
    }
  }

  private trackTTFB(): void {
    try {
      const observer = new PerformanceObserver(list => {
        const entries = list.getEntries();
        entries.forEach((entry: PerformanceEntry) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const perfEntry = entry as any;
          if (perfEntry.entryType === 'navigation') {
            this.recordMetric({
              name: 'TTFB',
              value: perfEntry.responseStart - perfEntry.requestStart,
              timestamp: Date.now(),
              url: window.location.href,
              userAgent: navigator.userAgent,
            });
          }
        });
      });

      observer.observe({ entryTypes: ['navigation'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('TTFB tracking not supported:', error);
    }
  }

  // Navigation timing
  private trackNavigationTiming(): void {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const navigation = performance.getEntriesByType(
          'navigation'
        )[0] as PerformanceNavigationTiming;

        if (navigation) {
          // DNS lookup time
          this.recordMetric({
            name: 'DNS_LOOKUP',
            value: navigation.domainLookupEnd - navigation.domainLookupStart,
            timestamp: Date.now(),
            url: window.location.href,
            userAgent: navigator.userAgent,
          });

          // TCP connection time
          this.recordMetric({
            name: 'TCP_CONNECTION',
            value: navigation.connectEnd - navigation.connectStart,
            timestamp: Date.now(),
            url: window.location.href,
            userAgent: navigator.userAgent,
          });

          // DOM content loaded
          this.recordMetric({
            name: 'DOM_CONTENT_LOADED',
            value: navigation.domContentLoadedEventEnd - navigation.fetchStart,
            timestamp: Date.now(),
            url: window.location.href,
            userAgent: navigator.userAgent,
          });

          // Page load complete
          this.recordMetric({
            name: 'PAGE_LOAD_COMPLETE',
            value: navigation.loadEventEnd - navigation.fetchStart,
            timestamp: Date.now(),
            url: window.location.href,
            userAgent: navigator.userAgent,
          });
        }
      }, 0);
    });
  }

  // Resource timing
  private trackResourceTiming(): void {
    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        // Track slow resources
        if (entry.duration > 1000) {
          // Resources taking more than 1 second
          this.recordMetric({
            name: 'SLOW_RESOURCE',
            value: entry.duration,
            timestamp: Date.now(),
            url: entry.name,
            userAgent: navigator.userAgent,
          });
        }
      });
    });

    observer.observe({ entryTypes: ['resource'] });
    this.observers.push(observer);
  }

  // Custom performance tracking
  trackCustomMetric(name: string, value: number): void {
    if (!this.isEnabled) return;

    this.recordMetric({
      name: `CUSTOM_${name.toUpperCase()}`,
      value,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  }

  // Track user interactions
  trackUserInteraction(
    action: string,
    element: string,
    duration?: number
  ): void {
    this.recordMetric({
      name: `USER_${action.toUpperCase()}`,
      value: duration || 0,
      timestamp: Date.now(),
      url: `${window.location.href}#${element}`,
      userAgent: navigator.userAgent,
    });
  }

  // Track API response times
  trackApiCall(
    endpoint: string,
    method: string,
    duration: number,
    status: number
  ): void {
    this.recordMetric({
      name: `API_${method.toUpperCase()}`,
      value: duration,
      timestamp: Date.now(),
      url: `${endpoint} (${status})`,
      userAgent: navigator.userAgent,
    });
  }

  // Memory usage tracking
  trackMemoryUsage(): void {
    if ('memory' in performance) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const memory = (performance as any).memory;
      this.recordMetric({
        name: 'MEMORY_USED',
        value: memory.usedJSHeapSize,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      });

      this.recordMetric({
        name: 'MEMORY_TOTAL',
        value: memory.totalJSHeapSize,
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
      });
    }
  }

  // Setup performance observers
  private setupPerformanceObservers(): void {
    // Track long tasks (> 50ms)
    try {
      const observer = new PerformanceObserver(list => {
        const entries = list.getEntries();
        entries.forEach((entry: PerformanceEntry) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const perfEntry = entry as any;
          this.recordMetric({
            name: 'LONG_TASK',
            value: perfEntry.duration,
            timestamp: Date.now(),
            url: window.location.href,
            userAgent: navigator.userAgent,
          });
        });
      });

      observer.observe({ entryTypes: ['longtask'] });
      this.observers.push(observer);
    } catch (error) {
      console.warn('Long task tracking not supported:', error);
    }
  }

  // Record metric
  private recordMetric(metric: PerformanceMetric): void {
    this.metrics.push(metric);

    // Send to analytics service (implement based on your analytics provider)
    this.sendToAnalytics(metric);

    // Log to console in development
    if (import.meta.env.DEV) {
      // console.log('Performance metric:', metric);
    }
  }

  // Send metrics to analytics service
  private sendToAnalytics(metric: PerformanceMetric): void {
    // This would integrate with your analytics service
    // For example: Google Analytics, Mixpanel, etc.

    // Example implementation for Google Analytics 4
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof (window as any).gtag !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).gtag('event', 'performance_metric', {
        metric_name: metric.name,
        metric_value: metric.value,
        page_url: metric.url,
      });
    }

    // Example implementation for custom analytics
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).analytics) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).analytics.track('Performance Metric', {
        name: metric.name,
        value: metric.value,
        url: metric.url,
        timestamp: metric.timestamp,
      });
    }
  }

  // Get performance summary
  getPerformanceSummary(): Record<string, PerformanceSummary> {
    const summary: Record<string, PerformanceSummary> = {};

    this.metrics.forEach(metric => {
      if (!summary[metric.name]) {
        summary[metric.name] = {
          count: 0,
          total: 0,
          min: Infinity,
          max: -Infinity,
          avg: 0,
        };
      }

      const stat = summary[metric.name];
      stat.count++;
      stat.total += metric.value;
      stat.min = Math.min(stat.min, metric.value);
      stat.max = Math.max(stat.max, metric.value);
      stat.avg = stat.total / stat.count;
    });

    return summary;
  }

  // Get recent metrics
  getRecentMetrics(minutes: number = 5): PerformanceMetric[] {
    const cutoff = Date.now() - minutes * 60 * 1000;
    return this.metrics.filter(metric => metric.timestamp > cutoff);
  }

  // Clear metrics
  clearMetrics(): void {
    this.metrics = [];
  }

  // Disable tracking
  disable(): void {
    this.isEnabled = false;
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }

  // Enable tracking
  enable(): void {
    this.isEnabled = true;
    this.setupPerformanceObservers();
  }
}

// Create singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Initialize Web Vitals tracking
export function initializePerformanceTracking(): void {
  performanceMonitor.trackWebVitals();

  // Track memory usage periodically
  setInterval(() => {
    performanceMonitor.trackMemoryUsage();
  }, 30000); // Every 30 seconds
}

// Utility functions for common performance tracking
export function trackPageLoad(pageName: string): void {
  const startTime = performance.now();

  window.addEventListener('load', () => {
    const loadTime = performance.now() - startTime;
    performanceMonitor.trackCustomMetric(`PAGE_LOAD_${pageName}`, loadTime);
  });
}

export function trackComponentRender(
  componentName: string,
  renderTime: number
): void {
  performanceMonitor.trackCustomMetric(
    `COMPONENT_RENDER_${componentName}`,
    renderTime
  );
}

export function trackUserAction(action: string, element: string): () => void {
  const startTime = performance.now();

  return () => {
    const duration = performance.now() - startTime;
    performanceMonitor.trackUserInteraction(action, element, duration);
  };
}

// React hook for performance tracking
export function usePerformanceTracking(componentName: string) {
  const trackRender = (renderTime: number) => {
    trackComponentRender(componentName, renderTime);
  };

  const trackAction = (action: string, element: string = componentName) => {
    return trackUserAction(action, element);
  };

  return {
    trackRender,
    trackAction,
    getMetrics: () => performanceMonitor.getPerformanceSummary(),
    getRecentMetrics: (minutes?: number) =>
      performanceMonitor.getRecentMetrics(minutes),
  };
}

// Performance budget monitoring
export function checkPerformanceBudget(): boolean {
  const summary = performanceMonitor.getPerformanceSummary();

  const budgets = {
    LCP: 2500, // 2.5 seconds
    FID: 100, // 100 milliseconds
    CLS: 0.1, // 0.1
    FCP: 1800, // 1.8 seconds
    TTFB: 800, // 800 milliseconds
  };

  let budgetPassed = true;

  Object.entries(budgets).forEach(([metric, budget]) => {
    // Check both regular metric names and CUSTOM_ prefixed versions (for testing)
    const stat = summary[metric] || summary[`CUSTOM_${metric}`];
    if (stat && stat.avg > budget) {
      console.warn(
        `Performance budget exceeded for ${metric}: ${stat.avg}ms > ${budget}ms`
      );
      budgetPassed = false;
    }
  });

  return budgetPassed;
}

// Export types
export type { PerformanceMetric, WebVitalsMetric };
